/**
 * Dictado por voz (2026-10-20): en el mostrador las manos están ocupadas. Llena el campo y NO envía:
 * la regente revisa lo que entendió antes de mandar.
 *
 * Apagado por defecto (VITE_DICTADO=false): en Chrome de Android el reconocimiento de voz lo hace un
 * servicio de Google, así que se prende solo con el acuerdo de Hipermaxi (DICTADO=true en Dokploy y
 * volver a desplegar). Sin soporte del navegador, el botón no aparece.
 */
import { useEffect, useRef, useState } from 'react'
import { Icon } from './icons'

interface ResultadoVoz {
  readonly isFinal: boolean
  readonly 0: { readonly transcript: string }
}
interface EventoVoz {
  readonly resultIndex: number
  readonly results: ArrayLike<ResultadoVoz>
}
interface Reconocedor {
  lang: string
  interimResults: boolean
  continuous: boolean
  onresult: ((e: EventoVoz) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
  start: () => void
  stop: () => void
}
type ConstructorReconocedor = new () => Reconocedor

export const DICTADO_ACTIVO = import.meta.env.VITE_DICTADO === 'true'

function reconocedor(): ConstructorReconocedor | null {
  const w = window as unknown as { SpeechRecognition?: ConstructorReconocedor; webkitSpeechRecognition?: ConstructorReconocedor }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

interface Props {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  /** Para los tests: forzar el interruptor sin variable de entorno. */
  activo?: boolean
}

export function Dictado({ value, onChange, disabled = false, activo = DICTADO_ACTIVO }: Props) {
  const [escuchando, setEscuchando] = useState(false)
  const rec = useRef<Reconocedor | null>(null)
  const base = useRef('')

  useEffect(() => () => rec.current?.stop(), [])

  const Clase = activo ? reconocedor() : null
  if (!Clase) return null

  const alternar = () => {
    if (escuchando) {
      rec.current?.stop()
      return
    }
    const r = new Clase()
    r.lang = 'es-BO'
    r.interimResults = true
    r.continuous = false
    base.current = value.trim() ? value.trim() + ' ' : ''
    r.onresult = (e) => {
      let texto = ''
      for (let i = 0; i < e.results.length; i++) texto += e.results[i][0].transcript
      onChange(base.current + texto.trim())
    }
    r.onend = () => setEscuchando(false)
    r.onerror = () => setEscuchando(false)
    rec.current = r
    setEscuchando(true)
    r.start()
  }

  return (
    <button
      type="button"
      aria-label={escuchando ? 'Dejar de dictar' : 'Dictar'}
      aria-pressed={escuchando}
      title={escuchando ? 'Escuchando… tocá para terminar' : 'Dictar en vez de escribir'}
      onClick={alternar}
      disabled={disabled}
      className="grid place-items-center transition-colors"
      style={{
        minHeight: 44,
        minWidth: 44,
        width: 44,
        height: 44,
        borderRadius: 999,
        border: `1px solid ${escuchando ? 'var(--color-danger)' : 'var(--color-border-strong)'}`,
        background: escuchando ? 'var(--color-danger-soft)' : 'var(--color-surface)',
        color: escuchando ? 'var(--color-danger)' : 'var(--color-text-muted)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Icon.Mic />
    </button>
  )
}
